/**
 * Contextual WhatsApp handoff message for the AutoMapa widget (M5).
 * Respects locale copy + currency formatting when provided.
 */
import { formatMoney as formatMoneyDefault } from "../../intl/formatters.js";
import { AUTOMAP_WHATSAPP_WIDGET_COPY_FALLBACK } from "./automapWhatsappWidgetCopy";
import { formatVehicleTitle } from "../automapListing/listingFormatters";

function pickText(value) {
  if (value == null) return null;
  const text = String(value).trim();
  return text || null;
}

function formatWhatsappDisplay(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return null;
  if (digits.startsWith("57") && digits.length >= 12) {
    return `+${digits.slice(0, 2)} ${digits.slice(2)}`;
  }
  return `+${digits}`;
}

function formatPriceLine(vehicle, formatMoney, displayCurrency, locale) {
  if (!vehicle) return null;
  if (vehicle.priceLabel) return pickText(vehicle.priceLabel);
  const amount = vehicle.price;
  if (amount == null || amount === "") return null;
  const numeric = Number(String(amount).replace(/[^\d.]/g, ""));
  if (!Number.isFinite(numeric)) {
    return pickText(String(amount));
  }
  const currency = displayCurrency || vehicle.currency || "COP";
  try {
    const formatter = typeof formatMoney === "function" ? formatMoney : formatMoneyDefault;
    return formatter(numeric, {
      displayCurrency: currency,
      locale: locale || undefined,
      showApproximation: false,
    });
  } catch {
    return `${numeric.toLocaleString(locale || "es-CO")} ${currency}`;
  }
}

function formatDownPayment(financing, formatMoney, displayCurrency, locale) {
  const amount = financing?.downPaymentAmount;
  if (amount == null || amount === "") return null;
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return null;
  // Amounts are stored in COP; format with active display currency when available.
  const currency = displayCurrency || financing.downPaymentCurrency || "COP";
  try {
    const formatter = typeof formatMoney === "function" ? formatMoney : formatMoneyDefault;
    return formatter(numeric, {
      displayCurrency: currency,
      storageCurrency: "COP",
      locale: locale || undefined,
      showApproximation: false,
    });
  } catch {
    return `$${numeric.toLocaleString(locale || "es-CO")} ${currency}`;
  }
}

function resolveVehicleTitle(vehicle) {
  if (!vehicle) return null;
  const canonical = formatVehicleTitle(vehicle, { fallback: "" });
  return canonical || pickText(vehicle.title) || null;
}

function isUserFacingReference(value) {
  const text = pickText(value);
  if (!text) return false;
  if (/^LD[-_]/i.test(text)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(text)) return false;
  return text.length <= 40;
}

function resolveHandoffCopy(copy) {
  if (copy?.handoff) return copy.handoff;
  const fallback = AUTOMAP_WHATSAPP_WIDGET_COPY_FALLBACK;
  return {
    success: fallback.handoffSuccess,
    error: fallback.handoffError,
    vehicleInterest: fallback.handoffVehicleInterest,
    general: fallback.handoffGeneral,
    price: fallback.handoffPrice,
    location: fallback.handoffLocation,
    listing: fallback.handoffListing,
    myDetails: fallback.handoffMyDetails,
    financing: fallback.handoffFinancing,
    interest: fallback.handoffInterest,
    downPayment: fallback.handoffDownPayment,
    term: fallback.handoffTerm,
    notes: fallback.handoffNotes,
    reference: fallback.handoffReference,
    termMonths: (months) =>
      String(fallback.handoffTermMonths || "{months} meses").replace(
        "{months}",
        String(months)
      ),
  };
}

function financingInterestLabel(interest, copy) {
  if (interest === "yes") return copy?.financingYes || "Sí";
  if (interest === "no") return copy?.financingNo || "No";
  if (interest === "unsure") return copy?.financingUnsure || "No estoy seguro";
  return String(interest || "");
}

/**
 * @param {{
 *   payload?: object|null,
 *   phase?: "draft"|"success"|"error",
 *   referenceId?: string|null,
 *   formatMoney?: Function|null,
 *   displayCurrency?: string|null,
 *   locale?: string|null,
 *   copy?: object|null,
 * }} [options]
 * @returns {string}
 */
export function buildAutomapWhatsappHandoffMessage({
  payload = null,
  phase = "draft",
  referenceId = null,
  formatMoney = null,
  displayCurrency = null,
  locale = null,
  copy = null,
} = {}) {
  const handoff = resolveHandoffCopy(copy);
  const vehicle = payload?.vehicle || null;
  const buyer = payload?.buyer || null;
  const financing = payload?.financing || null;
  const notes = pickText(payload?.notes);
  const lines = [];

  if (phase === "success") {
    lines.push(handoff.success);
  } else if (phase === "error") {
    lines.push(handoff.error);
  } else if (resolveVehicleTitle(vehicle)) {
    lines.push(handoff.vehicleInterest);
  } else {
    lines.push(handoff.general);
  }

  const vehicleTitle = resolveVehicleTitle(vehicle);
  if (vehicleTitle) {
    if (phase === "success" || phase === "error") {
      lines.push("");
      lines.push(`${copy?.labelVehicle || "Vehículo"}:`);
    }
    lines.push(vehicleTitle);
    const price = formatPriceLine(vehicle, formatMoney, displayCurrency, locale);
    if (price) lines.push(`${handoff.price}: ${price}`);
    const city = pickText(vehicle.city);
    if (city) lines.push(`${handoff.location}: ${city}`);
    const listingUrl = pickText(vehicle.listingUrl);
    if (listingUrl) lines.push(`${handoff.listing}: ${listingUrl}`);
  }

  const buyerLines = [];
  const name = pickText(buyer?.name);
  const whatsapp = formatWhatsappDisplay(buyer?.whatsapp);
  const email = pickText(buyer?.email);
  if (name) buyerLines.push(`${copy?.labelName || "Nombre"}: ${name}`);
  if (whatsapp) buyerLines.push(`${copy?.labelWhatsapp || "WhatsApp"}: ${whatsapp}`);
  if (email) buyerLines.push(`${copy?.labelEmail || "Correo"}: ${email}`);
  if (buyerLines.length) {
    lines.push("");
    lines.push(`${handoff.myDetails}:`);
    lines.push(...buyerLines);
  }

  const financingLines = [];
  const interest = financing?.interested;
  if (interest) {
    financingLines.push(`${handoff.interest}: ${financingInterestLabel(interest, copy)}`);
  }
  if (interest === "yes" || interest === "unsure") {
    const downPayment = formatDownPayment(financing, formatMoney, displayCurrency, locale);
    if (downPayment) financingLines.push(`${handoff.downPayment}: ${downPayment}`);
    if (financing?.preferredLoanTermMonths) {
      const termLabel =
        typeof handoff.termMonths === "function"
          ? handoff.termMonths(financing.preferredLoanTermMonths)
          : `${financing.preferredLoanTermMonths} meses`;
      financingLines.push(`${handoff.term}: ${termLabel}`);
    }
  }
  if (financingLines.length) {
    lines.push("");
    lines.push(`${handoff.financing}:`);
    lines.push(...financingLines);
  }

  if (notes) {
    lines.push("");
    lines.push(`${handoff.notes}:`);
    lines.push(notes);
  }

  if ((phase === "success" || phase === "error") && isUserFacingReference(referenceId)) {
    lines.push("");
    lines.push(`${handoff.reference}: ${pickText(referenceId)}`);
  }

  return lines.join("\n").trim();
}

export function resolveAutomapWhatsappHandoffReference(submissionResult = null) {
  if (!submissionResult) return null;
  const candidates = [
    submissionResult.raw?.referenceId,
    submissionResult.referenceId,
    submissionResult.leadId,
  ];
  for (const candidate of candidates) {
    if (isUserFacingReference(candidate)) {
      return pickText(candidate);
    }
  }
  return null;
}
